import PropTypes from 'prop-types'
import React from 'react'
import styled from 'react-emotion'
import isPropValid from '@emotion/is-prop-valid'
import pickBy from 'lodash/pickBy'
import { color, border, space, rowHeight, borderRadius } from '../../style'
import { Icon } from 'common/components/Icon'

export const FormElement = styled(
  React.forwardRef(({ className, Component, ...props }, ref) => {
    Component = Component || 'span'
    props = pickBy(props, (value, key) => isPropValid(key))

    return <Component className={className} ref={ref} {...props} />
  })
)(
  ({
    large,
    extraLarge,
    disabled,
    invalid,
    stretch,
    shrink,
    highlight,
    grey,
    multiline,
    rounded,
    underline,
    borderless
  }) => {
    let style = {
      label: 'FormElement',
      color: disabled ? color.greyLight : color.black,
      fontSize: extraLarge && 16,
      display: 'flex', // needed for alignItems
      alignItems: 'center', // vertically center text
      lineHeight: 1,
      textDecoration: 'none',
      height: extraLarge
        ? rowHeight.extraLarge
        : large
        ? rowHeight.large
        : rowHeight.default,
      padding: `0 ${
        extraLarge ? space.large : large ? space.default : space.small
      }px`,
      whiteSpace: 'nowrap',

      // override button defaults
      textAlign: 'left',
      borderRadius: rounded || large || extraLarge ? borderRadius : 0,
      border: border,
      background: highlight || grey ? color.greyBlock : 'none',

      '& > *:not(:last-child)': {
        marginRight: space.small
      },

      flexShrink: shrink ? 1 : 0,

      flex: stretch && 1,
      '& > *': stretch && {
        // things flexing can break the centering
        flex: 'none'
      }
    }

    if (invalid) style.color = color.red

    if (multiline) {
      style = {
        ...style,
        alignItems: 'flex-start',
        lineHeight: 1.3,
        height: 'auto',
        paddingTop: 8,
        paddingBottom: 8,
        whiteSpace: 'initial',
        wordBreak: 'break-word',
        flexDirection: 'column'
      }
    }

    if (borderless) style.border = 'none'

    if (underline) style.borderBottom = `1px solid ${color.black} !important`

    return style
  }
)

export const MenuButton = styled(
  React.forwardRef(({ onClick, className }, ref) => (
    <div onClick={onClick} className={className}>
      <Icon innerRef={ref} icon="more" />
    </div>
  ))
)(({ menuShowing }) => ({
  display: 'flex',
  width: 30,
  minWidth: 30, // stops long-name items squishing this
  height: 30,
  marginRight: -10,
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  '&:hover': { background: color.hover },
  background: menuShowing && color.hover
}))

const switchSize = 10
const borderWidth = 2
FormElement.Switch = styled('span')(({ on }) => ({
  flex: 'none',
  display: 'inline-block',
  width: switchSize * 2,
  height: switchSize,
  borderRadius: switchSize / 2,
  borderWidth,
  borderStyle: 'solid',
  borderColor: color.black,
  background: on ? color.black : 'transparent',

  position: 'relative',
  '&:after': {
    content: '""',
    display: on ? 'none' : 'block',
    position: 'absolute',
    left: -borderWidth,
    top: -borderWidth,
    width: switchSize,
    height: switchSize,
    borderRadius: switchSize / 2,
    background: color.black
  }
}))
FormElement.Switch.propTypes = {
  on: PropTypes.bool
}
FormElement.Switch.displayName = 'FormElement.Switch'

FormElement.Swatch = styled('span')(({ color: swatchColor }) => ({
  display: 'inline-block',
  width: 10,
  height: 10,
  borderRadius: 2,
  border: `1px solid ${color.black}`,
  background: swatchColor || '#ccc'
}))
FormElement.Swatch.propTypes = {
  color: PropTypes.string
}
FormElement.Swatch.displayName = 'FormElement.Swatch'

FormElement.Radio = styled('span')(({ on }) => ({
  label: 'FormElementRadio',
  display: 'inline-block',
  width: 10,
  height: 10,
  borderRadius: 10 / 2,
  border: `2px solid ${color.black}`,
  background: on && color.black
}))
FormElement.Radio.propTypes = {
  on: PropTypes.bool
}
FormElement.Radio.displayName = 'FormElement.Radio'

FormElement.Spacer = styled('span')({ flex: 1 }) //, padding: 0 })
FormElement.Spacer.displayName = 'FormElement.Spacer'

export const StyledFormElement = FormElement
export default FormElement
